import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import YachtCard from "./YachtCard";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];

export const useBrands = () =>
  useQuery({ queryKey: ["brands"], queryFn: async () => (await api.get("/brands")).data });

export const useAllBrands = () =>
  useQuery({ queryKey: ["brands", "all"], queryFn: async () => (await api.get("/admin/brands")).data });

export default function Yachts() {
  const { data: brands = [] } = useBrands();
  const { data: featured = [] } = useQuery({
    queryKey: ["yachts", "featured"],
    queryFn: async () => (await api.get("/yachts", { params: { featured: true } })).data,
  });
  const brandName = (slug) => brands.find((b) => b.slug === slug)?.name || slug;
  const brandOrigin = (slug) => brands.find((b) => b.slug === slug)?.origin || "";
  const totalModels = brands.reduce((n, b) => n + (b.model_count || 0), 0);

  return (
    <section id="yachts" className="relative bg-navy px-6 py-20 lg:px-12 lg:py-28" data-testid="yachts-section">
      <div className="mx-auto max-w-7xl">
        <div className="mb-20 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs"
              data-testid="yachts-overline"
            >
              03 — New Yachts
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="max-w-2xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl"
              data-testid="yachts-heading"
            >
              Highlights of the
              <span className="italic text-gold-light"> collection.</span>
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-sm"
          >
            <p className="text-sm font-light leading-relaxed text-mist" data-testid="yachts-description">
              A flagship from each of our partner shipyards. Explore the full range of
              {totalModels ? ` ${totalModels} ` : " "}new-build models by marque.
            </p>
            <Link
              to="/yachts"
              data-testid="yachts-browse-all-link"
              className="mt-6 inline-flex items-center gap-2 border border-gold/60 px-6 py-3 text-[11px] uppercase tracking-[0.28em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy"
            >
              Browse all new yachts <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>

        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
          {featured.map((y, i) => (
            <YachtCard
              key={y.id}
              yacht={y}
              index={i}
              span={SPANS[i % SPANS.length]}
              brandLabel={`${brandName(y.brand_slug)} · ${brandOrigin(y.brand_slug)}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
